import React, {useContext, useEffect} from 'react';
import cx from 'classnames';
import {Icon, PriorityIcon, Text, Title} from '@priority-software/priority-style-react';
import {AIInput} from '../../../../../components/AIInput/AIInput';
import {ReactComponent as AIAutomationSvg} from './assets/ai-automation.svg';
import {ReactComponent as AIAutomationErrorSvg} from './assets/ai-automation-error.svg';
import {AutomationContext} from '../../../context';
import {getAISuggestedRule} from '../../../../../services/rest/llm/aiSuggestedAutomationRule';
import {Loader} from '../../../../OnboardingDialog/components';
import {getRuleKeyByValue, supportAIBlock} from '../../../utils';
import {mapAIResponseToRule, prepareFieldsData} from './utils';
import {RuleType} from '../../../enums';
import {getBpmStatusesAsChooseList} from '../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import texts from '../../../../../Localization/AISearch/texts';
import styles from './styles.module.scss';
import {monitorAction} from '../../../../../services/datadog';
import {AIRulesMonitoring} from './enums';
import {fetchDateChooses} from '../../RuleDialog/parts/AutomationRuleCondition/parts/DateControl/utils';

type Props = {
    maxWidth?: number | string;
    showOnlyInput?: boolean;
    componentClasses?: string;
    onAIInputBlur?: () => void;
    onAIInputFocus?: () => void;
    onLoadingChange?: (flag: boolean) => void;
    type?: RuleType;
    defaultQuery?: string;
    session?: string;
};

export const AIAskBlock = (props: Props) => {
    const {
        maxWidth,
        showOnlyInput,
        componentClasses,
        onAIInputBlur,
        onAIInputFocus,
        onLoadingChange,
        type,
        session
    } = props;

    const {
        formName,
        aiGeneratedRuleQuery = '',
        choosesOptions,
        openRuleDialog,
        handleAIRuleSuggestions,
        automationType,
        fromStatus,
        toStatus
    } = useContext(AutomationContext);
    const isPendingRef = React.useRef(false);
    const [isError, setIsError] = React.useState(false);
    const [isQueryIssue, setIsQueryIssue] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [value, setValue] = React.useState(aiGeneratedRuleQuery);

    const focusRef = React.useRef<HTMLInputElement>(null);
    const {
        Fields,
        TimeUnits: timeUnits,
        DateFields: dateFields,
        User,
        Groups,
        Webhooks,
        MailFields
    } = choosesOptions;

    const isBPMPath = Boolean(fromStatus && toStatus);

    const onSearch = async () => {
        try {
            if (!isPendingRef.current) {
                isPendingRef.current = true;
                setIsLoading(true);
                onLoadingChange?.(true);
                setIsQueryIssue(false);
                setIsError(false);

                monitorAction(AIRulesMonitoring.submitBtn, {
                    ruleType: getRuleKeyByValue(automationType)
                });

                const fields = prepareFieldsData(Fields, automationType === RuleType.bpm);
                const isOldAPI = type === RuleType.bpm;
                const dateFieldID = isOldAPI ? -1 : fields.find(f => f.type.includes('Date'))?.id;

                const [res, predefinedDateList] = await Promise.all([
                    getAISuggestedRule(
                        {
                            query: value,
                            formName,
                            columns: fields,
                            fromStatus,
                            toStatus,
                            users: User?.map(({label}) => label as string) || [],
                            groups: Groups?.map(({label}) => label as string) || [],
                            webhooks: Webhooks?.map(({label}) => label as string) || [],
                            recipientFields: (MailFields || [])
                                .map(({label}) => fields.find(field => label === field.title))
                                .filter(Boolean)
                        },
                        type,
                        isBPMPath
                    ),
                    fetchDateChooses(session, dateFieldID, isOldAPI)
                ]);

                // query error
                if (res?.code === 7 || res?.code === 2) {
                    setIsQueryIssue(true);
                    setIsError(true);

                    monitorAction(AIRulesMonitoring.aiNoResult, {
                        ruleType: getRuleKeyByValue(automationType),
                        query: value
                    });
                    return;
                }

                openRuleDialog();
                handleAIRuleSuggestions({
                    openRule: mapAIResponseToRule(res, type, {
                        fields,
                        timeUnits,
                        dateFields,
                        webhooks: Webhooks,
                        users: User,
                        groups: Groups,
                        predefinedDateList,
                        availableStatuses:
                            type === RuleType.bpm ? getBpmStatusesAsChooseList(toStatus) : []
                    }),
                    aiGeneratedRuleQuery: value
                });
            }
        } catch (e) {
            console.error('getAISuggestedRule error:', e);
            setIsError(true);
        } finally {
            setIsLoading(false);
            isPendingRef.current = false;
            onLoadingChange?.(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    const getErrorText = () => {
        if (isQueryIssue) {
            return texts.ai_box_result_error_title;
        }
        return texts.ai_box_service_error_title;
    };

    const getTitle = () => {
        if (isError) {
            return getErrorText();
        }
        return texts.ai_box_title;
    };

    const renderTitleBlock = () => {
        if (showOnlyInput) return null;

        const icon = isError ? (
            <AIAutomationErrorSvg width={115} height={115} />
        ) : (
            <AIAutomationSvg width={169} height={133} />
        );

        return (
            <>
                {isLoading ? <Loader width={100} height={100} /> : icon}
                <Title skin="h2">{getTitle()}</Title>
            </>
        );
    };

    const getErrorMessage = () => {
        if (isError) {
            return (
                <Text
                    tag="div"
                    textColor="validation-red"
                    skin="tiny"
                    componentClasses={styles.errorHint}
                >
                    {getErrorText()}
                </Text>
            );
        }
        return null;
    };

    useEffect(() => {
        focusRef.current?.focus();
    }, []);

    if (!supportAIBlock()) return null;

    return (
        <div
            className={cx(styles.component, componentClasses, {
                [styles.extraPadding]: showOnlyInput
            })}
            style={{maxWidth}}
        >
            {renderTitleBlock()}

            <AIInput
                ref={focusRef}
                onBlur={onAIInputBlur}
                onFocus={onAIInputFocus}
                handleKeyDown={handleKeyDown}
                onChange={handleChange}
                onSearch={onSearch}
                placeholder={texts.ai_box_search_placeholder}
                value={value}
                submitText={
                    <Icon
                        icon={PriorityIcon.paperPlane}
                        componentClasses={cx(styles.gradientColor, styles.submitBtn)}
                        hoverable={true}
                    />
                }
            />

            {!showOnlyInput ? (
                <Text tag="div" textColor="secondary-500" skin="tiny">
                    {texts.ai_box_disclaimer}
                </Text>
            ) : (
                getErrorMessage()
            )}
        </div>
    );
};
